import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Activity, AlertTriangle, BarChart3, Ban, CheckCircle2, ChevronLeft, ChevronRight,
  Copy, Gauge, KeyRound, LayoutDashboard, ListChecks, Lock, LogOut, RefreshCw, Search,
  ShieldCheck, Trash2, Users, X, Zap,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import { adminApi, apiErr, getToken, setToken } from "./api";

const RED = "#E50914";
const GOLD = "#E7C477";
const CHART_COLORS = ["#E50914", "#E7C477", "#38BDF8", "#22C55E", "#F59E0B"];

const PERM_LABELS = {
  generate_keys: "Generate Keys", revoke_keys: "Revoke Keys", extend_keys: "Extend Keys",
  approve_users: "Approve Users", remove_users: "Remove Users", manage_subadmins: "Manage Sub-Admins",
  block_user_email: "Per-User Block", global_email_block: "Global Block", view_logs: "View Logs",
  view_stats: "View Stats", manage_keys: "Manage Keys", view_dashboard: "View Dashboard",
  broadcast: "Broadcast",
};

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, perm: "view_dashboard" },
  { id: "users", label: "Users", icon: Users, perm: "view_dashboard" },
  { id: "approvals", label: "Approvals", icon: ListChecks, perm: "approve_users" },
  { id: "keys", label: "Keys", icon: KeyRound, perm: "view_dashboard" },
  { id: "blocks", label: "Blocks", icon: Ban, perm: "block_user_email" },
  { id: "logs", label: "Logs", icon: Activity, perm: "view_logs" },
  { id: "stats", label: "Statistics", icon: BarChart3, perm: "view_stats" },
  { id: "system", label: "System Status", icon: Gauge, perm: "view_dashboard" },
];

const can = (admin, perm) => admin?.role === "main_admin" || (admin?.permissions || []).includes(perm);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// --------------------------------------------------------------------------- //
// Small UI atoms
// --------------------------------------------------------------------------- //

function Badge({ tone = "muted", children }) {
  const tones = {
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    unused: "bg-[#E7C477]/15 text-[#E7C477] border-[#E7C477]/30",
    expired: "bg-white/10 text-[#A3A3AC] border-white/15",
    revoked: "bg-[#E50914]/15 text-[#FF6B6B] border-[#E50914]/30",
    muted: "bg-white/10 text-[#A3A3AC] border-white/15",
    red: "bg-[#E50914]/15 text-[#FF6B6B] border-[#E50914]/30",
    gold: "bg-[#E7C477]/15 text-[#E7C477] border-[#E7C477]/30",
    blue: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.muted}`}>
      {children}
    </span>
  );
}

function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid={testid}>
      <motion.div className="w-full max-w-md rounded-xl border border-white/10 bg-[#131317] p-6 shadow-2xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-[#A3A3AC] hover:text-white" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-[#6E6E7A]">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full rounded-lg border border-white/15 bg-[#0B0B0E] px-3 py-2 text-sm text-white placeholder:text-[#52525E] outline-none transition-colors focus:border-[#E50914]";
const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-lg bg-[#E50914] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#F40612] disabled:opacity-50";
const btnGhost = "inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-[#18181E] px-3 py-2 text-sm text-[#E0E0E6] transition-colors hover:border-white/20 hover:bg-[#22222B]";

// --------------------------------------------------------------------------- //
// Login
// --------------------------------------------------------------------------- //

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await adminApi.post("/login", { email: email.trim(), password });
      setToken(data.access_token);
      onLogin(data.admin);
      toast.success("Welcome back");
    } catch (e2) {
      setErr(apiErr(e2, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#08080A] p-4"
      style={{ backgroundImage: "radial-gradient(ellipse 70% 50% at 50% -8%, rgba(229,9,20,0.16), transparent 70%)" }}>
      <motion.form onSubmit={submit} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#131317]/90 p-8 backdrop-blur-md" data-testid="admin-login-form">
        <div className="mb-6 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#E50914]/15 text-[#E50914]"><ShieldCheck size={20} /></span>
          <div>
            <div className="text-lg font-extrabold tracking-tight text-white">Anuragh <span className="text-[#E50914]">Admin</span></div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6E7A]">Command Center</div>
          </div>
        </div>
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls}
            placeholder="you@example.com" autoComplete="username" data-testid="admin-email" />
        </Field>
        <Field label="Password">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls}
            placeholder="••••••••" autoComplete="current-password" data-testid="admin-password" />
        </Field>
        {err ? <div className="mb-3 rounded-lg border border-[#E50914]/30 bg-[#E50914]/10 px-3 py-2 text-sm text-[#FF6B6B]" data-testid="admin-login-error">{err}</div> : null}
        <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="admin-login-submit">
          {busy ? <RefreshCw size={16} className="animate-spin" /> : <Lock size={16} />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <a href="/" className="mt-4 block text-center text-xs text-[#6E6E7A] hover:text-white">← Back to site</a>
      </motion.form>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Sections
// --------------------------------------------------------------------------- //

function StatCard({ label, value, icon: Icon, tone = "muted" }) {
  const toneCls = { red: "text-[#E50914]", gold: "text-[#E7C477]", green: "text-emerald-400", blue: "text-sky-400", muted: "text-white" };
  return (
    <div className="rounded-xl border border-white/10 bg-[#131317] p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#6E6E7A]">{label}</span>
        {Icon ? <Icon size={16} className="text-[#6E6E7A]" /> : null}
      </div>
      <div className={`mt-2 text-3xl font-extrabold ${toneCls[tone]}`}>{value}</div>
    </div>
  );
}

function Overview({ admin }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await adminApi.get("/overview")).data); } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-6" data-testid="section-overview">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Users" value={data.total_users} icon={Users} />
        <StatCard label="Searches Today" value={data.searches_today} icon={Search} tone="blue" />
        <StatCard label="Success Rate" value={`${data.success_rate}%`} icon={CheckCircle2} tone="green" />
        <StatCard label="Active Keys" value={data.active_keys} icon={KeyRound} tone="gold" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Approved Users" value={data.approved_users} />
        <StatCard label="Sub Admins" value={data.sub_admins} />
        <StatCard label="Unused Keys" value={data.unused_keys} tone="gold" />
        <StatCard label="Revoked Keys" value={data.revoked_keys} tone="red" />
      </div>
      <Panel title="Recent Activity">
        <Table head={["Action", "Target", "By", "Platform", "When"]}>
          {(data.recent_activity || []).map((l) => (
            <tr key={l.id} className="border-t border-white/5" data-testid="admin-table-row">
              <td className="px-4 py-2"><Badge tone={l.success ? "muted" : "red"}>{l.action}</Badge></td>
              <td className="px-4 py-2 font-mono text-xs text-[#A3A3AC]">{l.target || "—"}</td>
              <td className="px-4 py-2 text-[#A3A3AC]">{l.performed_by_username || "—"}</td>
              <td className="px-4 py-2 text-[#A3A3AC]">{l.platform}</td>
              <td className="px-4 py-2 text-xs text-[#6E6E7A]">{fmtDate(l.timestamp)}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function UsersSection() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/users", { params: { q: q || undefined, page, limit } });
      setRows(data.users); setTotal(data.total);
    } catch (e) { toast.error(apiErr(e)); }
  }, [q, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="section-users">
      <SearchBar value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search username or user ID…" testid="users-search" />
      <Panel title={`Users (${total})`}>
        <Table head={["User ID", "Username", "Role", "Approved", "Last Seen"]}>
          {rows.map((u) => (
            <tr key={u.id} className="border-t border-white/5" data-testid="admin-table-row">
              <td className="px-4 py-2 font-mono text-xs text-white">{u.user_id}</td>
              <td className="px-4 py-2 text-[#A3A3AC]">{u.username ? `@${u.username}` : "—"}</td>
              <td className="px-4 py-2"><Badge tone={u.role === "sub_admin" ? "gold" : u.role === "main_admin" ? "red" : "muted"}>{u.role}</Badge></td>
              <td className="px-4 py-2">{u.approved ? <Badge tone="active">yes</Badge> : <Badge>no</Badge>}</td>
              <td className="px-4 py-2 text-xs text-[#6E6E7A]">{fmtDate(u.last_seen)}</td>
            </tr>
          ))}
        </Table>
        <Pager page={page} total={total} limit={limit} onPage={setPage} />
      </Panel>
    </div>
  );
}

function Approvals({ admin }) {
  const [target, setTarget] = useState("");
  const [role, setRole] = useState("approved_user");
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState(false);
  const togglePerm = (p) => setPerms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const run = async (endpoint, body, okMsg) => {
    setBusy(true);
    try { await adminApi.post(endpoint, body); toast.success(okMsg); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="section-approvals">
      <Panel title="Approve / Manage Access">
        <div className="space-y-4 p-4">
          <Field label="Target (@username or numeric user ID)">
            <input value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls} placeholder="@user or 123456789" data-testid="approve-target" />
          </Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="approve-role">
              <option value="approved_user">Approved User</option>
              <option value="sub_admin">Sub-Admin</option>
            </select>
          </Field>
          {role === "sub_admin" ? (
            <div className="rounded-lg border border-white/10 bg-[#0B0B0E] p-3">
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-[#6E6E7A]">Granted Permissions</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(PERM_LABELS).map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs text-[#A3A3AC]">
                    <input type="checkbox" checked={perms.includes(p)} onChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                    {PERM_LABELS[p]}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button disabled={busy || !target} className={btnPrimary} data-testid="approve-submit"
              onClick={() => run("/users/approve", { target: target.trim(), role, permissions: perms }, "User approved")}>
              <CheckCircle2 size={16} /> Approve
            </button>
            {can(admin, "manage_subadmins") ? (
              <button disabled={busy || !target} className={btnGhost} data-testid="promote-submit"
                onClick={() => run("/users/promote", { target: target.trim(), permissions: perms }, "Promoted to sub-admin")}>
                <ShieldCheck size={16} /> Promote Sub-Admin
              </button>
            ) : null}
            {can(admin, "remove_users") ? (
              <button disabled={busy || !target} className={btnGhost} data-testid="unapprove-submit"
                onClick={() => run("/users/unapprove", { target: target.trim() }, "Access removed")}>
                <Ban size={16} /> Unapprove
              </button>
            ) : null}
            {can(admin, "manage_subadmins") ? (
              <button disabled={busy || !target} className={btnGhost} data-testid="demote-submit"
                onClick={() => run("/users/demote", { target: target.trim() }, "Sub-admin removed")}>
                <Trash2 size={16} /> Remove Sub-Admin
              </button>
            ) : null}
          </div>
          <p className="text-xs text-[#6E6E7A]">Approve by numeric ID works even before the user has started the bot — the approval is recognised automatically on their first use.</p>
        </div>
      </Panel>
    </div>
  );
}

function Keys({ admin }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const limit = 20;
  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/keys", { params: { status: status || undefined, q: q || undefined, page, limit } });
      setRows(data.keys); setTotal(data.total);
    } catch (e) { toast.error(apiErr(e)); }
  }, [status, q, page]);
  useEffect(() => { load(); }, [load]);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Key copied"); };
  const doRevoke = async (key) => { try { await adminApi.post("/keys/revoke", { key }); toast.success("Key revoked"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doExtend = async (key, duration) => { try { await adminApi.post("/keys/extend", { key, duration }); toast.success("Key extended"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-4" data-testid="section-keys">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBar value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search key…" testid="keys-search" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={`${inputCls} max-w-[160px]`} data-testid="keys-filter-status">
          <option value="">All statuses</option>
          <option value="unused">Unused</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
        {can(admin, "generate_keys") ? (
          <button className={`${btnPrimary} ml-auto`} onClick={() => setGenOpen(true)} data-testid="admin-generate-key-btn"><Zap size={16} /> Generate Key</button>
        ) : null}
      </div>
      <Panel title={`Keys (${total})`}>
        <Table head={["Key", "Type", "Status", "Platform", "Created By", "Expires", ""]}>
          {rows.map((k) => (
            <tr key={k.id} className="border-t border-white/5" data-testid="admin-table-row">
              <td className="px-4 py-2 font-mono text-xs text-white">{k.raw_key}</td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{k.duration_label}</td>
              <td className="px-4 py-2"><Badge tone={k.status}>{k.status}</Badge></td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{k.generated_platform}</td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{k.created_by_username || "—"}</td>
              <td className="px-4 py-2 text-xs text-[#6E6E7A]">{k.is_permanent ? "Never" : fmtDate(k.expires_at)}</td>
              <td className="px-4 py-2">
                <div className="flex justify-end gap-1">
                  <button className="rounded p-1 text-[#A3A3AC] hover:text-white" title="Copy" onClick={() => copy(k.raw_key)} data-testid="key-copy"><Copy size={14} /></button>
                  {can(admin, "extend_keys") ? <button className="rounded p-1 text-[#A3A3AC] hover:text-[#E7C477]" title="Extend" onClick={() => setConfirm({ type: "extend", key: k.raw_key })} data-testid="key-extend"><RefreshCw size={14} /></button> : null}
                  {can(admin, "revoke_keys") && k.status !== "revoked" ? <button className="rounded p-1 text-[#A3A3AC] hover:text-[#FF6B6B]" title="Revoke" onClick={() => setConfirm({ type: "revoke", key: k.raw_key })} data-testid="key-revoke"><Ban size={14} /></button> : null}
                </div>
              </td>
            </tr>
          ))}
        </Table>
        <Pager page={page} total={total} limit={limit} onPage={setPage} />
      </Panel>

      <AnimatePresence>
        {genOpen ? <GenerateKeyModal onClose={() => setGenOpen(false)} onDone={() => { setGenOpen(false); load(); }} /> : null}
      </AnimatePresence>
      <AnimatePresence>
        {confirm ? (
          <ConfirmModal
            title={confirm.type === "revoke" ? "Revoke key?" : "Extend key"}
            danger={confirm.type === "revoke"}
            extend={confirm.type === "extend"}
            keyValue={confirm.key}
            onClose={() => setConfirm(null)}
            onConfirm={(dur) => { confirm.type === "revoke" ? doRevoke(confirm.key) : doExtend(confirm.key, dur); setConfirm(null); }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function GenerateKeyModal({ onClose, onDone }) {
  const [keyType, setKeyType] = useState("30d");
  const [custom, setCustom] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/keys/generate", { key_type: keyType, custom: custom || undefined, assigned_username: username || undefined });
      setGenerated(data.key);
      toast.success("Key generated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const botUser = "theanuragh_bot";
  return (
    <Modal open onClose={onClose} title="Generate Access Key" testid="generate-key-modal">
      {generated ? (
        <div className="space-y-3" data-testid="generated-key-card">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="mb-1 flex items-center gap-2 text-emerald-400"><CheckCircle2 size={16} /> Key Generated Successfully</div>
            <div className="font-mono text-lg font-bold text-white">{generated.raw_key}</div>
            <div className="mt-2 space-y-1 text-sm text-[#A3A3AC]">
              <div>Validity: <b className="text-white">{generated.duration_label}</b></div>
              <div>Expires: <b className="text-white">{generated.is_permanent ? "Never (Lifetime)" : (generated.expires_at ? fmtDate(generated.expires_at) : "On first use")}</b></div>
              <div>Status: <Badge tone="unused">unused</Badge></div>
              <div>Created by: <b className="text-white">{generated.created_by_username}</b></div>
            </div>
            <div className="mt-3 border-t border-white/10 pt-2 text-xs text-[#6E6E7A]">
              Works on website or Telegram: <b className="text-[#E7C477]">@{botUser}</b>
            </div>
          </div>
          <div className="flex gap-2">
            <button className={btnGhost} onClick={() => { navigator.clipboard?.writeText(generated.raw_key); toast.success("Copied"); }}><Copy size={14} /> Copy</button>
            <button className={`${btnPrimary} flex-1`} onClick={onDone} data-testid="generate-done">Done</button>
          </div>
        </div>
      ) : (
        <div>
          <Field label="Key Type">
            <select value={keyType} onChange={(e) => setKeyType(e.target.value)} className={inputCls} data-testid="gen-key-type">
              <option value="1d">1 Day</option><option value="3d">3 Days</option><option value="7d">7 Days</option>
              <option value="15d">15 Days</option><option value="30d">30 Days</option><option value="90d">90 Days</option>
              <option value="permanent">Permanent / Lifetime</option><option value="custom">Custom…</option>
            </select>
          </Field>
          {keyType === "custom" ? (
            <Field label="Custom duration (e.g. 12h, 45d, 2w)">
              <input value={custom} onChange={(e) => setCustom(e.target.value)} className={inputCls} placeholder="45d" data-testid="gen-custom" />
            </Field>
          ) : null}
          <Field label="Assign to username (optional)">
            <input value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} placeholder="@username" data-testid="gen-username" />
          </Field>
          <button disabled={busy} className={`${btnPrimary} w-full`} onClick={submit} data-testid="gen-submit">
            {busy ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />} Generate
          </button>
        </div>
      )}
    </Modal>
  );
}

function ConfirmModal({ title, danger, extend, keyValue, onClose, onConfirm }) {
  const [dur, setDur] = useState("7d");
  return (
    <Modal open onClose={onClose} title={title} testid="confirm-modal">
      <p className="mb-3 text-sm text-[#A3A3AC]">Key <span className="font-mono text-white">{keyValue}</span></p>
      {extend ? (
        <Field label="Extend by (e.g. 7d, 12h, 30d)">
          <input value={dur} onChange={(e) => setDur(e.target.value)} className={inputCls} data-testid="confirm-duration" />
        </Field>
      ) : (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#E50914]/30 bg-[#E50914]/10 p-3 text-sm text-[#FF6B6B]">
          <AlertTriangle size={16} /> This revokes the key and ends any active sessions.
        </div>
      )}
      <div className="flex gap-2">
        <button className={btnGhost} onClick={onClose} data-testid="confirm-cancel">Cancel</button>
        <button className={`${danger ? "bg-[#E50914] hover:bg-[#F40612]" : "bg-[#E7C477] text-black hover:bg-[#f0d089]"} flex-1 rounded-lg px-4 py-2 text-sm font-bold text-white transition-colors`}
          onClick={() => onConfirm(dur)} data-testid="confirm-ok">Confirm</button>
      </div>
    </Modal>
  );
}

function Blocks({ admin }) {
  const [data, setData] = useState({ user_blocks: [], global_blocks: [] });
  const [gEmail, setGEmail] = useState(""); const [gReason, setGReason] = useState("");
  const [uId, setUId] = useState(""); const [uEmail, setUEmail] = useState("");
  const load = useCallback(async () => { try { setData((await adminApi.get("/blocks")).data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (endpoint, body, msg) => { try { await adminApi.post(endpoint, body); toast.success(msg); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="section-blocks">
      {can(admin, "global_email_block") ? (
        <Panel title="Global Email Blocks">
          <div className="space-y-2 p-4">
            <input value={gEmail} onChange={(e) => setGEmail(e.target.value)} className={inputCls} placeholder="email@example.com" data-testid="global-block-email" />
            <input value={gReason} onChange={(e) => setGReason(e.target.value)} className={inputCls} placeholder="Reason (optional)" data-testid="global-block-reason" />
            <button className={btnPrimary} data-testid="global-block-submit" onClick={() => act("/blocks/global", { email: gEmail.trim(), reason: gReason || undefined }, "Globally blocked")}><Ban size={16} /> Block Globally</button>
          </div>
          <Table head={["Email", "Reason", ""]}>
            {data.global_blocks.map((b) => (
              <tr key={b.id} className="border-t border-white/5" data-testid="admin-table-row">
                <td className="px-4 py-2 text-sm text-white">{b.email}</td>
                <td className="px-4 py-2 text-xs text-[#A3A3AC]">{b.reason || "—"}</td>
                <td className="px-4 py-2 text-right"><button className="rounded p-1 text-[#A3A3AC] hover:text-[#FF6B6B]" onClick={() => act("/blocks/global/remove", { email: b.email }, "Unblocked")} data-testid="global-unblock"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : <div />}
      {can(admin, "block_user_email") ? (
        <Panel title="Per-User Email Blocks">
          <div className="space-y-2 p-4">
            <input value={uId} onChange={(e) => setUId(e.target.value)} className={inputCls} placeholder="Numeric user ID" data-testid="user-block-id" />
            <input value={uEmail} onChange={(e) => setUEmail(e.target.value)} className={inputCls} placeholder="email@example.com" data-testid="user-block-email" />
            <button className={btnPrimary} data-testid="user-block-submit" onClick={() => act("/blocks/user", { user_id: Number(uId), email: uEmail.trim() }, "User blocked for email")}><Ban size={16} /> Block</button>
          </div>
          <Table head={["User ID", "Email", ""]}>
            {data.user_blocks.map((b) => (
              <tr key={b.id} className="border-t border-white/5" data-testid="admin-table-row">
                <td className="px-4 py-2 font-mono text-xs text-white">{b.user_id}</td>
                <td className="px-4 py-2 text-sm text-[#A3A3AC]">{b.email}</td>
                <td className="px-4 py-2 text-right"><button className="rounded p-1 text-[#A3A3AC] hover:text-[#FF6B6B]" onClick={() => act("/blocks/user/remove", { user_id: b.user_id, email: b.email }, "Unblocked")} data-testid="user-unblock"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : <div />}
    </div>
  );
}

function Logs() {
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [q, setQ] = useState(""); const [platform, setPlatform] = useState("");
  const limit = 25;
  const load = useCallback(async () => {
    try { const { data } = await adminApi.get("/logs", { params: { q: q || undefined, platform: platform || undefined, page, limit } }); setRows(data.logs); setTotal(data.total); }
    catch (e) { toast.error(apiErr(e)); }
  }, [q, platform, page]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="section-logs">
      <div className="flex flex-wrap gap-2">
        <SearchBar value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search by user or target…" testid="logs-search" />
        <select value={platform} onChange={(e) => { setPlatform(e.target.value); setPage(1); }} className={`${inputCls} max-w-[160px]`} data-testid="logs-platform">
          <option value="">All platforms</option><option value="website">Website</option><option value="telegram">Telegram</option>
        </select>
      </div>
      <Panel title={`Audit Logs (${total})`}>
        <Table head={["Action", "Target", "By", "Role", "Platform", "OK", "When"]}>
          {rows.map((l) => (
            <tr key={l.id} className="border-t border-white/5" data-testid="admin-table-row">
              <td className="px-4 py-2 text-xs text-white">{l.action}</td>
              <td className="px-4 py-2 font-mono text-xs text-[#A3A3AC]">{l.target || "—"}</td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{l.performed_by_username || "—"}</td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{l.performed_by_role}</td>
              <td className="px-4 py-2 text-xs text-[#A3A3AC]">{l.platform}</td>
              <td className="px-4 py-2">{l.success ? <CheckCircle2 size={14} className="text-emerald-400" /> : <X size={14} className="text-[#FF6B6B]" />}</td>
              <td className="px-4 py-2 text-xs text-[#6E6E7A]">{fmtDate(l.timestamp)}</td>
            </tr>
          ))}
        </Table>
        <Pager page={page} total={total} limit={limit} onPage={setPage} />
      </Panel>
    </div>
  );
}

function Statistics() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { setData((await adminApi.get("/stats")).data); } catch (e) { toast.error(apiErr(e)); } })(); }, []);
  if (!data) return <Loading />;
  const platformData = Object.entries(data.platforms).map(([name, value]) => ({ name, value }));
  const kindData = Object.entries(data.kinds).map(([name, value]) => ({ name, value }));
  return (
    <div className="space-y-6" data-testid="section-stats">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Search Volume (7 days)">
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={RED} stopOpacity={0.5} /><stop offset="100%" stopColor={RED} stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="day" stroke="#6E6E7A" fontSize={11} /><YAxis stroke="#6E6E7A" fontSize={11} />
                <RTooltip contentStyle={{ background: "#131317", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Area type="monotone" dataKey="total" stroke={RED} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Success vs Failed (7 days)">
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="day" stroke="#6E6E7A" fontSize={11} /><YAxis stroke="#6E6E7A" fontSize={11} />
                <RTooltip contentStyle={{ background: "#131317", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="success" stackId="a" fill="#22C55E" /><Bar dataKey="failed" stackId="a" fill={RED} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Platform Usage">
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={platformData.length ? platformData : [{ name: "none", value: 1 }]} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {(platformData.length ? platformData : [{ name: "none" }]).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <RTooltip contentStyle={{ background: "#131317", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title={`Search Type Usage · Avg ${data.avg_response_ms}ms`}>
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kindData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" stroke="#6E6E7A" fontSize={11} /><YAxis type="category" dataKey="name" stroke="#6E6E7A" fontSize={11} width={70} />
                <RTooltip contentStyle={{ background: "#131317", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="value" fill={GOLD} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function System() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { setData((await adminApi.get("/system")).data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const Row = ({ label, ok, note }) => (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#131317] px-4 py-3" data-testid={`system-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className="text-sm text-[#E0E0E6]">{label}</span>
      <span className="flex items-center gap-2">
        {note ? <span className="text-xs text-[#6E6E7A]">{note}</span> : null}
        <Badge tone={ok ? "active" : "red"}>{ok ? "Online" : "Offline"}</Badge>
      </span>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="section-system">
      <div className="mb-2 flex justify-end"><button className={btnGhost} onClick={load}><RefreshCw size={14} /> Refresh</button></div>
      <Row label="Backend API" ok={data.backend} />
      <Row label="Database" ok={data.database} />
      <Row label="IMAP Mailbox" ok={data.imap_configured ? data.imap_reachable : false}
        note={data.imap_configured ? (data.imap_latency_ms != null ? `${data.imap_latency_ms}ms` : "unreachable") : "not configured"} />
      <Row label="Telegram Bot" ok={data.telegram_bot_configured} note={data.telegram_bot_configured ? "" : "token not set"} />
      <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#131317] px-4 py-3">
        <span className="text-sm text-[#E0E0E6]">Errors (24h)</span>
        <Badge tone={data.recent_errors ? "red" : "active"}>{data.recent_errors}</Badge>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Shared layout atoms
// --------------------------------------------------------------------------- //

const Loading = () => <div className="flex items-center gap-2 py-16 text-[#6E6E7A]"><RefreshCw size={16} className="animate-spin" /> Loading…</div>;
const Panel = ({ title, children }) => (
  <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0F0F12]">
    <div className="border-b border-white/10 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[#A3A3AC]">{title}</div>
    {children}
  </div>
);
const Table = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm">
      <thead><tr className="text-[10px] uppercase tracking-wider text-[#6E6E7A]">{head.map((h, i) => <th key={i} className="px-4 py-2 font-mono font-medium">{h}</th>)}</tr></thead>
      <tbody>{React.Children.count(children) ? children : <tr><td colSpan={head.length} className="px-4 py-8 text-center text-[#6E6E7A]">No records</td></tr>}</tbody>
    </table>
  </div>
);
function SearchBar({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative flex-1 min-w-[200px]">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E7A]" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} pl-9`} data-testid={testid} />
    </div>
  );
}
function Pager({ page, total, limit, onPage }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-white/10 px-4 py-2 text-xs text-[#6E6E7A]">
      <span>Page {page} / {pages}</span>
      <div className="flex gap-1">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded border border-white/10 p-1 disabled:opacity-40" data-testid="pager-prev"><ChevronLeft size={14} /></button>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="rounded border border-white/10 p-1 disabled:opacity-40" data-testid="pager-next"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Shell
// --------------------------------------------------------------------------- //

function Shell({ admin, onLogout }) {
  const [active, setActive] = useState("overview");
  const visibleNav = useMemo(() => NAV.filter((n) => can(admin, n.perm) || (n.id === "blocks" && can(admin, "global_email_block")) || (n.id === "approvals" && can(admin, "manage_subadmins"))), [admin]);
  const Section = { overview: Overview, users: UsersSection, approvals: Approvals, keys: Keys, blocks: Blocks, logs: Logs, stats: Statistics, system: System }[active];
  return (
    <div className="flex min-h-screen bg-[#08080A] text-white">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-white/10 bg-[#0B0B0F]/95 p-4 md:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#E50914]/15 text-[#E50914]"><ShieldCheck size={18} /></span>
          <div><div className="text-sm font-extrabold tracking-tight">Anuragh <span className="text-[#E50914]">Admin</span></div></div>
        </div>
        <nav className="flex-1 space-y-1">
          {visibleNav.map((n) => (
            <button key={n.id} onClick={() => setActive(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${active === n.id ? "bg-[#E50914]/15 text-white" : "text-[#A3A3AC] hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="px-3 text-xs text-[#6E6E7A]">{admin.email}</div>
          <div className="px-3 pb-2"><Badge tone={admin.role === "main_admin" ? "red" : "gold"}>{admin.role}</Badge></div>
          <button onClick={onLogout} className={`${btnGhost} w-full`} data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-white/10 bg-[#0B0B0F]/80 px-6 backdrop-blur-md">
          <h1 className="text-lg font-bold capitalize">{NAV.find((n) => n.id === active)?.label}</h1>
          <div className="flex items-center gap-2">
            <select className="rounded-lg border border-white/10 bg-[#18181E] px-2 py-1.5 text-sm text-[#A3A3AC] md:hidden" value={active} onChange={(e) => setActive(e.target.value)} data-testid="admin-nav-mobile">
              {visibleNav.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
            <a href="/" className={btnGhost}>View Site</a>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-5 sm:p-8">
          <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <Section admin={admin} />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [admin, setAdmin] = useState(undefined); // undefined=checking, null=logged out
  useEffect(() => {
    if (!getToken()) { setAdmin(null); return; }
    adminApi.get("/me").then(({ data }) => setAdmin(data)).catch(() => { setToken(""); setAdmin(null); });
  }, []);
  const logout = () => { setToken(""); setAdmin(null); toast("Signed out"); };
  return (
    <>
      <Toaster position="top-right" theme="dark" richColors />
      {admin === undefined ? (
        <div className="flex min-h-screen items-center justify-center bg-[#08080A]"><Loading /></div>
      ) : admin ? (
        <Shell admin={admin} onLogout={logout} />
      ) : (
        <Login onLogin={setAdmin} />
      )}
    </>
  );
}
